import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Stack,
  Paper,
  Card,
  CardContent,
  Chip,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  Grid,
  Avatar,
  IconButton,
  Breadcrumbs,
  Link,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  GridView as GridIcon,
  ViewList as ListIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  AccessTime as TimeIcon,
  Tag as TagIcon,
  TrendingUp as TrendingIcon,
  Home as HomeIcon,
  NavigateNext,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useSearch } from '../contexts/SearchContext';
import { searchAllContent, getAllTagsWithCounts, getTrendingPosts } from '../utils/searchUtils';
import SearchComponent from '../components/SearchComponent';
import { authors } from '../data/blogPosts';

const POSTS_PER_PAGE = 9;

const SearchResultsPage = () => {
  const {
    searchQuery,
    selectedTags,
    selectedAuthor,
    sortBy,
    hasActiveFilters,
  } = useSearch();

  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [showSidebar] = useState(true);

  const trendingPosts = getTrendingPosts(6);
  const allTags = getAllTagsWithCounts();

  // Perform search whenever search parameters change
  useEffect(() => {
    setIsLoading(true);
    setCurrentPage(1);
    
    const timer = setTimeout(() => {
      const results = searchAllContent({
        query: searchQuery,
        tags: selectedTags,
        author: selectedAuthor,
        sortBy,
        contentTypes: ['blog', 'docs'],
      });
      
      setSearchResults(results);
      setIsLoading(false);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery, selectedTags, selectedAuthor, sortBy]);

  // Calculate pagination
  const totalPages = Math.ceil(searchResults.length / POSTS_PER_PAGE);
  const startIndex = (currentPage - 1) * POSTS_PER_PAGE;
  const endIndex = startIndex + POSTS_PER_PAGE;
  const currentResults = searchResults.slice(startIndex, endIndex);

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getReadingTime = (post) => {
    if (post.readingTime) return post.readingTime;
    const wordsPerMinute = 200;
    const words = post.content.split(' ').length;
    return Math.ceil(words / wordsPerMinute);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const ResultCard = ({ post, variant = 'grid' }) => {
    const author = authors[post.authors[0]];
    
    if (variant === 'list') {
      return (
        <Card 
          elevation={0}
          sx={{ 
            mb: 2,
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              borderColor: 'primary.main',
              boxShadow: 2,
            },
            transition: 'all 0.2s ease',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Grid container spacing={3}>
              {/* Image Column */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Box
                  component="img"
                  src={post.image}
                  alt={post.title}
                  sx={{
                    width: '100%',
                    height: 120,
                    objectFit: 'cover',
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                />
                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <Chip 
                    label={post.contentType === 'blog' ? 'Blog' : 'Docs'} 
                    size="small" 
                    color={post.contentType === 'blog' ? 'primary' : 'secondary'}
                    variant="outlined"
                  />
                  {post.section && (
                    <Chip 
                      label={post.section} 
                      size="small" 
                      variant="outlined"
                    />
                  )}
                </Box>
              </Grid>
              
              {/* Content Column */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Stack spacing={2}>
                  <Box>
                    <Typography 
                      variant="h5" 
                      component={RouterLink}
                      to={post.url}
                      sx={{ 
                        textDecoration: 'none',
                        color: 'text.primary',
                        '&:hover': { color: 'primary.main' },
                        fontWeight: 600,
                        lineHeight: 1.3,
                      }}
                    >
                      {post.title}
                    </Typography>
                  </Box>
                  
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                    sx={{ 
                      lineHeight: 1.6,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                    }}
                  >
                    {post.excerpt}
                  </Typography>

                  <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        src={author.image}
                        alt={author.name}
                        sx={{ width: 24, height: 24 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {author.name}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {formatDate(post.date)}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <TimeIcon sx={{ fontSize: 16 }} />
                      <Typography variant="body2" color="text.secondary">
                        {getReadingTime(post)} min read
                      </Typography>
                    </Box>
                  </Stack>
                </Stack>
              </Grid>
              
              {/* Actions Column */}
              <Grid size={{ xs: 12, md: 3 }}>
                <Stack spacing={2} sx={{ height: '100%', justifyContent: 'space-between' }}>
                  <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                    {post.tags.slice(0, 4).map((tag) => (
                      <Chip
                        key={tag}
                        label={tag}
                        component={RouterLink}
                        to={`/blog/tag/${tag}`}
                        clickable
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    ))}
                    {post.tags.length > 4 && (
                      <Chip
                        label={`+${post.tags.length - 4}`}
                        size="small"
                        variant="outlined"
                        sx={{ fontSize: '0.75rem' }}
                      />
                    )}
                  </Stack>
                  
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <IconButton size="small">
                      <ShareIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small">
                      <BookmarkIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      );
    }

    return (
      <Card 
        elevation={0}
        sx={{ 
          height: '100%',
          border: '1px solid',
          borderColor: 'divider',
          '&:hover': {
            borderColor: 'primary.main',
            boxShadow: 4,
            transform: 'translateY(-2px)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <CardContent sx={{ p: 0, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* Image */}
          <Box
            component="img"
            src={post.image}
            alt={post.title}
            sx={{
              width: '100%',
              height: 160,
              objectFit: 'cover',
            }}
          />
          
          <Box sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
            <Stack spacing={2} sx={{ flexGrow: 1 }}>
              {/* Content Type Badge */}
              <Stack direction="row" spacing={1}>
                <Chip 
                  label={post.contentType === 'blog' ? 'Blog' : 'Docs'} 
                  size="small" 
                  color={post.contentType === 'blog' ? 'primary' : 'secondary'}
                  variant="filled"
                />
                {post.section && (
                  <Chip 
                    label={post.section} 
                    size="small" 
                    variant="outlined"
                  />
                )}
              </Stack>
              
              <Box sx={{ flexGrow: 1 }}>
                <Typography 
                  variant="h6" 
                  component={RouterLink}
                  to={post.url}
                  sx={{ 
                    textDecoration: 'none',
                    color: 'text.primary',
                    '&:hover': { color: 'primary.main' },
                    fontWeight: 600,
                    lineHeight: 1.3,
                    mb: 1,
                    display: 'block',
                  }}
                >
                  {post.title}
                </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ 
                  lineHeight: 1.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 3,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {post.excerpt}
              </Typography>
            </Box>

            <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
              {post.tags.slice(0, 3).map((tag) => (
                <Chip
                  key={tag}
                  label={tag}
                  component={RouterLink}
                  to={`/blog/tag/${tag}`}
                  clickable
                  size="small"
                  variant="outlined"
                  sx={{ fontSize: '0.7rem' }}
                />
              ))}
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Avatar
                  src={author.image}
                  alt={author.name}
                  sx={{ width: 20, height: 20 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {formatDate(post.date)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary">
                {getReadingTime(post)} min read
              </Typography>
            </Stack>
          </Stack>
          </Box>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs */}
      <Breadcrumbs 
        separator={<NavigateNext fontSize="small" />} 
        sx={{ mb: 4 }}
      >
        <Link
          component={RouterLink}
          to="/"
          sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', textDecoration: 'none' }}
        >
          <HomeIcon sx={{ mr: 0.5, fontSize: 18 }} />
          Home
        </Link>
        <Link
          component={RouterLink}
          to="/blog"
          sx={{ color: 'text.secondary', textDecoration: 'none' }}
        >
          Blog
        </Link>
        <Typography color="text.primary" sx={{ fontWeight: 500 }}>
          Search Results
        </Typography>
      </Breadcrumbs>

      <Grid container spacing={4}>
        {/* Main Content */}
        <Grid size={{ xs: 12, lg: showSidebar ? 8 : 12 }}>
          <Stack spacing={4}>
            {/* Search Component */}
            <Box>
              <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
                Search Blog Posts
              </Typography>
              <SearchComponent 
                variant="full"
                autoFocus={!hasActiveFilters()}
                onSearchSubmit={() => {}}
              />
            </Box>

            {/* Results Header */}
            <Box>
              <Stack 
                direction="row" 
                justifyContent="space-between" 
                alignItems="center"
                sx={{ mb: 2 }}
              >
                <Typography variant="h6">
                  {isLoading ? 'Searching...' : (
                    <>
                      {searchResults.length} result{searchResults.length !== 1 ? 's' : ''}
                      {searchQuery && ` for "${searchQuery}"`}
                    </>
                  )}
                </Typography>

                <Stack direction="row" spacing={1}>
                  <IconButton 
                    onClick={() => setViewMode('grid')}
                    color={viewMode === 'grid' ? 'primary' : 'default'}
                  >
                    <GridIcon />
                  </IconButton>
                  <IconButton 
                    onClick={() => setViewMode('list')}
                    color={viewMode === 'list' ? 'primary' : 'default'}
                  >
                    <ListIcon />
                  </IconButton>
                </Stack>
              </Stack>

              {hasActiveFilters() && (
                <Alert 
                  severity="info" 
                  sx={{ mb: 2 }}
                  icon={<FilterIcon />}
                >
                  Search filters are active. {' '}
                  <Button size="small" onClick={() => window.location.reload()}>
                    Clear all filters
                  </Button>
                </Alert>
              )}
            </Box>

            {/* Results */}
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress />
              </Box>
            ) : searchResults.length === 0 ? (
              <Paper sx={{ p: 6, textAlign: 'center' }}>
                <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  No results found
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                  {hasActiveFilters() 
                    ? 'Try adjusting your search filters or search terms.'
                    : 'Try different search terms or browse our latest posts below.'
                  }
                </Typography>
                <Button 
                  variant="outlined" 
                  component={RouterLink} 
                  to="/blog"
                  startIcon={<SearchIcon />}
                >
                  Browse All Posts
                </Button>
              </Paper>
            ) : (
              <>
                {viewMode === 'grid' ? (
                  <Grid container spacing={3}>
                    {currentResults.map((post) => (
                      <Grid size={{ xs: 12, sm: 6, lg: 4 }} key={post.slug}>
                        <ResultCard post={post} variant="grid" />
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Stack spacing={2}>
                    {currentResults.map((post) => (
                      <ResultCard key={post.slug} post={post} variant="list" />
                    ))}
                  </Stack>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        </Grid>

        {/* Sidebar */}
        {showSidebar && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Stack spacing={3}>
              {/* Popular Tags */}
              <Paper sx={{ p: 3 }}>
                <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TagIcon />
                  Popular Tags
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                  {allTags.slice(0, 12).map(({ tag, count }) => (
                    <Chip
                      key={tag}
                      label={`${tag} (${count})`}
                      component={RouterLink}
                      to={`/blog/tag/${tag}`}
                      clickable
                      variant={selectedTags.includes(tag) ? 'filled' : 'outlined'}
                      color={selectedTags.includes(tag) ? 'primary' : 'default'}
                      size="small"
                    />
                  ))}
                </Stack>
              </Paper>

              {/* Trending Posts */}
              {!hasActiveFilters() && (
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <TrendingIcon />
                    Trending Posts
                  </Typography>
                  <Stack spacing={2}>
                    {trendingPosts.slice(0, 4).map((post) => (
                      <Box key={post.slug}>
                        <Typography 
                          variant="body2" 
                          component={RouterLink}
                          to={`/blog/${post.slug}`}
                          sx={{ 
                            textDecoration: 'none',
                            color: 'text.primary',
                            '&:hover': { color: 'primary.main' },
                            fontWeight: 500,
                            lineHeight: 1.3,
                          }}
                        >
                          {post.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                          {formatDate(post.date)} • {getReadingTime(post)} min read
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Paper>
              )}
            </Stack>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default SearchResultsPage;